// src/components/MyNFT.js
import React, { useState, useEffect } from 'react';
import { ArrowLeft, RefreshCw, ExternalLink, Image as ImageIcon, AlertCircle, Search } from 'lucide-react';
import './MyNFT.css';

// 고정된 NFT 컨트랙트 주소
const NFT_CONTRACT_ADDRESS = '0xc3d64CBB90CA3A4ec30D4E2Acab9a01899552af8';

// IPFS 게이트웨이 목록
const IPFS_GATEWAYS = [
  'https://ipfs.io/ipfs/',
  'https://dweb.link/ipfs/',
  'https://cloudflare-ipfs.com/ipfs/',
  'https://gateway.pinata.cloud/ipfs/',
  'https://hardbin.com/ipfs/',
  'https://ipfs.eth.aragon.network/ipfs/'
];

// IPFS URI를 여러 게이트웨이 URL로 변환
const getIPFSUrls = (ipfsUri) => {
  if (!ipfsUri) return [];
  
  if (ipfsUri.startsWith('ipfs://')) {
    const hash = ipfsUri.replace('ipfs://', '');
    return IPFS_GATEWAYS.map(gateway => `${gateway}${hash}`);
  }
  
  return [ipfsUri];
};

// NFTCard 컴포넌트
const NFTCard = ({ nft, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const [metadata, setMetadata] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [imageUrlIndex, setImageUrlIndex] = useState(0);
  
  useEffect(() => {
    let isMounted = true;
    
    const fetchMetadata = async (uri) => {
      if (!uri) return;
      
      setIsLoadingMetadata(true);
      
      // 랜덤 지연 추가
      const delay = Math.random() * 2000 + 1000;
      await new Promise(resolve => setTimeout(resolve, delay));
      
      const metadataUrls = getIPFSUrls(uri);
      
      for (let i = 0; i < metadataUrls.length; i++) {
        if (!isMounted) break;
        
        try {
          const url = metadataUrls[i];
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          
          const response = await fetch(url, {
            signal: controller.signal,
            headers: { 'Accept': 'application/json' }
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok && isMounted) {
            const data = await response.json();
            setMetadata(data);
            break;
          } else if (response.status === 429) {
            console.warn('⚠️ Rate limit, 다음 게이트웨이 시도...');
            continue;
          }
        } catch (err) {
          if (err.name === 'AbortError') {
            console.warn('⏱️ 타임아웃, 다음 게이트웨이 시도...');
          }
          if (i < metadataUrls.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
            continue;
          }
        }
      }
      
      if (isMounted) {
        setIsLoadingMetadata(false);
      }
    };
    
    if (nft.tokenURI) {
      fetchMetadata(nft.tokenURI);
    }
    
    return () => {
      isMounted = false;
    };
  }, [nft.tokenURI]);

  // 메타데이터가 로드되면 이미지 URL 설정
  useEffect(() => {
    if (metadata?.image) {
      const imageUrls = getIPFSUrls(metadata.image);
      if (imageUrls.length > 0) {
        setCurrentImageUrl(imageUrls[0]);
        setImageUrlIndex(0);
      }
    }
  }, [metadata]);

  // 이미지 로드 실패 시 다음 게이트웨이 시도
  const handleImageError = () => {
    if (!metadata?.image) {
      setImageError(true);
      return;
    }

    const imageUrls = getIPFSUrls(metadata.image);
    const nextIndex = imageUrlIndex + 1;

    if (nextIndex < imageUrls.length) {
      console.log(`이미지 로드 실패, 다음 게이트웨이 시도 (${nextIndex + 1}/${imageUrls.length})`);
      setCurrentImageUrl(imageUrls[nextIndex]);
      setImageUrlIndex(nextIndex);
    } else {
      setImageError(true);
    }
  };

  const name = metadata?.name || nft.tokenName || `Token #${nft.tokenId}`;
  const description = metadata?.description || '';

  return (
    <div className="nft-card" onClick={onClick}>
      <div className="nft-image-container">
        {isLoadingMetadata ? (
          <div className="nft-image-placeholder">
            <div className="spinner-small"></div>
            <span className="loading-text">로딩 중...</span>
          </div>
        ) : currentImageUrl && !imageError ? (
          <img 
            src={currentImageUrl} 
            alt={name}
            onError={handleImageError}
            className="nft-image"
            loading="lazy"
          />
        ) : (
          <div className="nft-image-placeholder">
            <ImageIcon className="placeholder-icon" />
            <span className="token-id">#{nft.tokenId}</span>
          </div>
        )}
      </div>
      <div className="nft-info">
        <h3 className="nft-name" title={name}>{name}</h3>
        {description && (
          <p className="nft-description" title={description}>
            {description.length > 60 ? description.substring(0, 60) + '...' : description}
          </p>
        )}
        {/* 소유자 주소 표시 */}
        <p className="nft-owner" title={nft.owner}>
          소유자: {nft.owner.substring(0, 6)}...{nft.owner.substring(nft.owner.length - 4)}
        </p>

        <p className="nft-contract">
          {nft.contractAddress.substring(0, 6)}...{nft.contractAddress.substring(nft.contractAddress.length - 4)}
        </p>
      </div>
    </div>
  );
};

// NFT 상세 정보 모달 컴포넌트
const NFTDetailModal = ({ nftDetail, onClose, isLoading }) => {
  const [currentImageUrl, setCurrentImageUrl] = useState(null);
  const [imageUrlIndex, setImageUrlIndex] = useState(0);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    if (nftDetail?.image) {
      const imageUrls = getIPFSUrls(nftDetail.image);
      if (imageUrls.length > 0) {
        setCurrentImageUrl(imageUrls[0]);
        setImageUrlIndex(0);
        setImageError(false);
      }
    }
  }, [nftDetail?.image]);

  const handleImageError = () => {
    if (!nftDetail?.image) {
      setImageError(true);
      return;
    }

    const imageUrls = getIPFSUrls(nftDetail.image);
    const nextIndex = imageUrlIndex + 1;

    if (nextIndex < imageUrls.length) {
      setCurrentImageUrl(imageUrls[nextIndex]);
      setImageUrlIndex(nextIndex);
    } else {
      setImageError(true);
    }
  };

  if (!nftDetail) return null;

  return (
    <div className="nft-detail-modal-overlay" onClick={onClose}>
      <div className="nft-detail-modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" onClick={onClose}>×</button>
        
        {isLoading ? (
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>상세 정보를 불러오는 중...</p>
          </div>
        ) : (
          <div className="modal-content-wrapper">
            {/* 1. 상단: Full Name */}
            <div className="modal-title-section">
              <h2 className="modal-nft-title">{nftDetail.fullName}</h2>
            </div>

            {/* 2. 중단: Image + 정보들 */}
            <div className="modal-middle-grid">
              {/* 왼쪽: 이미지 */}
              <div className="modal-image-section">
                {currentImageUrl && !imageError ? (
                  <img 
                    src={currentImageUrl} 
                    alt={nftDetail.fullName}
                    onError={handleImageError}
                    className="modal-nft-image"
                  />
                ) : (
                  <div className="modal-image-placeholder">
                    <ImageIcon size={80} />
                    <span className="modal-token-id">#{nftDetail.tokenId}</span>
                  </div>
                )}
              </div>

              {/* 오른쪽: 정보 블록들 */}
              <div className="modal-info-section">
                {/* 기본 정보 */}
                <div className="modal-info-block">
                  <h3>기본 정보</h3>
                  <div className="modal-info-row">
                    <span className="info-label">토큰 ID:</span>
                    <span className="info-value">#{nftDetail.tokenId}</span>
                  </div>
                  <div className="modal-info-row">
                    <span className="info-label">컨트랙트:</span>
                    <span className="info-value mono">
                      {nftDetail.contractAddress.substring(0, 10)}...{nftDetail.contractAddress.substring(nftDetail.contractAddress.length - 8)}
                    </span>
                  </div>
                </div>

                {/* 발행 정보 */}
                {nftDetail.mintInfo && (
                  <div className="modal-info-block">
                    <h3>발행 정보</h3>
                    <div className="modal-info-row">
                      <span className="info-label">발행자:</span>
                      <span className="info-value mono">
                        {nftDetail.mintInfo.minter.substring(0, 10)}...{nftDetail.mintInfo.minter.substring(nftDetail.mintInfo.minter.length - 8)}
                      </span>
                    </div>
                    <div className="modal-info-row">
                      <span className="info-label">발행일:</span>
                      <span className="info-value">{nftDetail.mintInfo.mintDate}</span>
                    </div>
                  </div>
                )}

                {/* 소유 정보 */}
                <div className="modal-info-block">
                  <h3>소유 정보</h3>
                  <div className="modal-info-row">
                    <span className="info-label">현재 소유자:</span>
                    <span className="info-value mono">
                      {nftDetail.owner.substring(0, 10)}...{nftDetail.owner.substring(nftDetail.owner.length - 8)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. 하단: 설명 */}
            {nftDetail.description && (
              <div className="modal-description-section">
                <h3>설명</h3>
                <p className="modal-description">{nftDetail.description}</p>
              </div>
            )}

            {/* 버튼들 */}
            <div className="modal-actions">
              <button 
                className="modal-action-btn primary"
                onClick={() => window.open(
                  `https://scan.worldland.foundation/token/${nftDetail.contractAddress}?a=${nftDetail.tokenId}`,
                  '_blank'
                )}
              >
                <ExternalLink size={16} />
                Explorer에서 보기
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MyNFT = ({ account, onBack }) => {
  const [nfts, setNfts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedNFT, setSelectedNFT] = useState(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [currentAddress, setCurrentAddress] = useState(account);
  const [debugInfo, setDebugInfo] = useState('');
  const [discoveredContracts, setDiscoveredContracts] = useState([]);
  
  // 모달 관련 상태 추가
  const [selectedNFTDetail, setSelectedNFTDetail] = useState(null);
  const [nftDetailLoading, setNftDetailLoading] = useState(false);

  // ERC-721 Transfer 이벤트 시그니처
  const TRANSFER_EVENT_SIGNATURE = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';

  // RPC 호출 헬퍼 함수
  const callRpc = async (method, params) => {
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    if (!provider) {
      throw new Error('Web3 provider를 찾을 수 없습니다.');
    }

    return await provider.request({
      method: method,
      params: params
    });
  };

  // 메타데이터 동기 조회 함수
  const fetchMetadataSync = async (uri) => {
    if (!uri) return null;
    
    const metadataUrls = getIPFSUrls(uri);
    
    for (let i = 0; i < metadataUrls.length; i++) {
      try {
        const url = metadataUrls[i];
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { 'Accept': 'application/json' }
        });
        
        clearTimeout(timeoutId);
        
        if (response.ok) {
          return await response.json();
        }
      } catch (err) {
        if (i < metadataUrls.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
          continue;
        }
      }
    }
    
    return null;
  };

  // NFT 상세 정보 조회 함수
  const fetchNFTDetailInfo = async (nft) => { 
    setNftDetailLoading(true);
    
    try {
      console.log('📋 NFT 상세 정보 조회 시작:', nft.tokenId);

      // 1. 메타데이터 로드
      let metadata = null;
      if (nft.tokenURI) {
        metadata = await fetchMetadataSync(nft.tokenURI);
      }

      // 2. getTokenDetails 함수 호출 (owner, uri, mintedAt, minter 한 번에 조회)
      const getTokenDetailsSelector = '0x5c9a63f6';
      const paddedTokenId = nft.tokenId.toString(16).padStart(64, '0');
      
      let owner = nft.owner;
      let mintInfo = null;

      try {
        const detailsResult = await callRpc('eth_call', [{
          to: nft.contractAddress,
          data: getTokenDetailsSelector + paddedTokenId
        }, 'latest']);

        if (detailsResult && detailsResult !== '0x') {
          // 결과 파싱
          const hex = detailsResult.substring(2);
          
          // owner (address - 32바이트, 실제로는 20바이트 주소)
          const ownerHex = hex.substring(24, 64);
          owner = '0x' + ownerHex;
          console.log('✅ 소유자:', owner);

          // uri offset (32바이트) - 건너뛰기
          // const uriOffset = parseInt(hex.substring(64, 128), 16);
          
          // mintedAt (uint256 - 32바이트)
          const mintedAtHex = hex.substring(128, 192);
          const mintedAt = parseInt(mintedAtHex, 16);
          console.log('✅ 발행 시간:', mintedAt);

          // minter (address - 32바이트, 실제로는 20바이트 주소)
          const minterHex = hex.substring(216, 256);
          const minter = '0x' + minterHex;
          console.log('✅ 발행자:', minter);

          // mintedAt이 0이 아니면 발행 정보 설정
          if (mintedAt > 0) {
            const mintDate = new Date(mintedAt * 1000);
            
            mintInfo = {
              minter,
              mintedAt,
              mintDate: mintDate.toLocaleString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              }),
              blockNumber: null,
              transactionHash: null
            };
            
            console.log('✅ 발행 정보:', mintInfo);
          }
        }
      } catch (detailsErr) {
        console.warn('⚠️ getTokenDetails 호출 실패, 대체 방법 사용:', detailsErr);
        owner = nft.owner || 'Unknown';
        
        // 대체 방법 2: Transfer 이벤트로 발행 정보 조회 (NFTGallery.js의 로직 재활용)
        try {
          const transferEventSignature = '0xddf252ad1be2c89b69c2b068fc378daa952ba7f163c4a11628f55a4df523b3ef';
          const paddedFrom = '0x0000000000000000000000000000000000000000000000000000000000000000';
          const paddedTokenIdForLog = nft.tokenId.toString(16).padStart(64, '0');
          
          const logs = await callRpc('eth_getLogs', [{
            fromBlock: '0x0',
            toBlock: 'latest',
            address: nft.contractAddress,
            topics: [
              transferEventSignature,
              paddedFrom,
              null,
              '0x' + paddedTokenIdForLog
            ]
          }]);

          if (logs && logs.length > 0) {
            const mintLog = logs[0];
            const minter = '0x' + mintLog.topics[2].substring(26);
            const blockNumber = parseInt(mintLog.blockNumber, 16);
            
            // 블록 정보로 타임스탬프 가져오기
            const block = await callRpc('eth_getBlockByNumber', [mintLog.blockNumber, false]);
            const timestamp = parseInt(block.timestamp, 16);
            const mintDate = new Date(timestamp * 1000);
            
            mintInfo = {
              minter,
              blockNumber,
              transactionHash: mintLog.transactionHash,
              mintedAt: timestamp,
              mintDate: mintDate.toLocaleString('ko-KR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit'
              })
            };
            
            console.log('✅ 발행 정보 (이벤트 대체):', mintInfo);
          }
        } catch (eventErr) {
          console.warn('⚠️ Transfer 이벤트 조회 실패:', eventErr);
        }
        
        // getTokenDetails가 실패했으므로, ownerOf도 다시 조회하여 최신 소유자를 확인
        try {
          const ownerOfSelector = '0x6352211e';
          const ownerResult = await callRpc('eth_call', [{
            to: nft.contractAddress,
            data: ownerOfSelector + paddedTokenId
          }, 'latest']);
          
          owner = '0x' + ownerResult.substring(ownerResult.length - 40);
          console.log('✅ 소유자 (대체):', owner);
        } catch (ownerErr) {
          console.warn('⚠️ 소유자 조회 실패 (대체):', ownerErr);
        }
      }

      // 3. 상세 정보 설정
      setSelectedNFTDetail({
        ...nft,
        metadata,
        owner: owner || nft.owner || 'Unknown',
        mintInfo,
        fullName: metadata?.name || nft.tokenName || `Token #${nft.tokenId}`,
        description: metadata?.description || '',
        image: metadata?.image || null,
        attributes: metadata?.attributes || []
      });

      console.log('✅ NFT 상세 정보 조회 완료');

    } catch (err) {
      console.error('❌ NFT 상세 정보 조회 실패:', err);
      alert('NFT 상세 정보를 불러오는데 실패했습니다: ' + err.message);
    } finally {
      setNftDetailLoading(false);
    }
  };

  // Explorer API를 통한 NFT 조회를 사용하지 않음
  // discoverNFTContracts 함수
  const discoverNFTContracts = async (address) => {
    console.log('고정된 NFT 컨트랙트 사용:', NFT_CONTRACT_ADDRESS);
    setDebugInfo('고정된 NFT 컨트랙트로 조회합니다...');
    setDiscoveredContracts([NFT_CONTRACT_ADDRESS]);
    return [NFT_CONTRACT_ADDRESS];
  };

  // 직접 컨트랙트 조회
  const fetchNFTsFromContracts = async (contracts, address) => {
    const provider = window.selectedEthereumProvider || window.ethereum;
    
    if (!provider) {
      throw new Error('Web3 provider를 찾을 수 없습니다.');
    }

    console.log(`컨트랙트 직접 조회 시작:`, contracts[0]);
    setDebugInfo(`컨트랙트에서 NFT를 조회하고 있습니다...`);

    const allNFTs = [];
    const contractAddress = contracts[0];

    try {
      // 1. balanceOf 호출
      const balanceOfSelector = '0x70a08231';
      const paddedAddress = address.substring(2).toLowerCase().padStart(64, '0');
      
      const balanceResult = await provider.request({
        method: 'eth_call',
        params: [{
          to: contractAddress,
          data: balanceOfSelector + paddedAddress
        }, 'latest']
      });

      const balance = parseInt(balanceResult, 16);
      console.log(`✅ NFT 잔액: ${balance}개`);
      
      if (balance === 0) {
        return [];
      }

      setDebugInfo(`NFT ${balance}개 발견. 상세 정보 조회 중...`);
      
      // 2. tokensOfOwner 호출 (tokenOfOwnerByIndex 대신)
      const tokensOfOwnerSelector = '0x8462151c'; // tokensOfOwner(address)
      
      const tokensResult = await provider.request({
        method: 'eth_call',
        params: [{
          to: contractAddress,
          data: tokensOfOwnerSelector + paddedAddress
        }, 'latest']
      });

      // 결과 파싱 (uint256[] 배열)
      const hex = tokensResult.substring(2);
      const offset = parseInt(hex.substring(0, 64), 16) * 2;
      const arrayLength = parseInt(hex.substring(offset, offset + 64), 16);
      
      console.log(`토큰 배열 길이: ${arrayLength}`);
      
      const tokenIds = [];
      for (let i = 0; i < arrayLength; i++) {
        const tokenIdHex = hex.substring(offset + 64 + (i * 64), offset + 64 + ((i + 1) * 64));
        const tokenId = parseInt(tokenIdHex, 16);
        tokenIds.push(tokenId);
        console.log(`토큰 ID ${i + 1}: ${tokenId}`);
      }

      // 3. 각 토큰의 URI 조회
      for (let i = 0; i < tokenIds.length; i++) {
        const tokenId = tokenIds[i];
        setDebugInfo(`토큰 정보 조회 중... (${i + 1}/${tokenIds.length})`);

        const ownerOfSelector = '0x6352211e';
        const paddedTokenId = tokenId.toString(16).padStart(64, '0');
        
        const ownerResult = await provider.request({
          method: 'eth_call',
          params: [{
            to: contractAddress,
            data: ownerOfSelector + paddedTokenId
          }, 'latest']
        });

        let ownerAddress = '0x' + ownerResult.substring(ownerResult.length - 40);
        console.log(`토큰 #${tokenId} 소유자: ${ownerAddress}`);
        
        try {
          const tokenURISelector = '0xc87b56dd';
          const paddedTokenId = tokenId.toString(16).padStart(64, '0');
          
          const uriResult = await provider.request({
            method: 'eth_call',
            params: [{
              to: contractAddress,
              data: tokenURISelector + paddedTokenId
            }, 'latest']
          });

          let tokenURI = '';
          if (uriResult && uriResult !== '0x') {
            const hex = uriResult.substring(2);
            const offset = parseInt(hex.substring(0, 64), 16) * 2;
            const length = parseInt(hex.substring(offset, offset + 64), 16) * 2;
            const data = hex.substring(offset + 64, offset + 64 + length);
            
            for (let j = 0; j < data.length; j += 2) {
              const byte = parseInt(data.substring(j, j + 2), 16);
              if (byte >= 32 && byte < 127) {
                tokenURI += String.fromCharCode(byte);
              }
            }
            
            console.log(`토큰 #${tokenId} URI: ${tokenURI}`);
          }

          allNFTs.push({
            contractAddress,
            tokenId,
            tokenURI: tokenURI.trim(),
            owner: ownerAddress,
            balance
          });
        } catch (uriErr) {
          console.warn(`토큰 ${tokenId} URI 조회 실패:`, uriErr);
        }
      }

      console.log(`✅ 총 ${allNFTs.length}개 NFT 조회 완료`);
      return allNFTs;
      
    } catch (err) {
      console.error(`컨트랙트 조회 실패:`, err);
      throw new Error(`컨트랙트 조회 실패: ${err.message}`);
    }
  };

  // 메인 NFT 조회 함수  
  const fetchNFTs = async (targetAddress = currentAddress) => {
    if (!targetAddress) {
      setError('주소가 설정되지 않았습니다.');
      return;
    }
    
    console.log('🚀 NFT 조회 시작 🚀');
    console.log('조회 대상 주소:', targetAddress);
    console.log('사용할 컨트랙트:', NFT_CONTRACT_ADDRESS);
    
    setIsLoading(true);
    setError(null);
    setDebugInfo('NFT를 직접 조회하고 있습니다...');
    setNfts([]);
    setDiscoveredContracts([NFT_CONTRACT_ADDRESS]);
    
    try {
      // 고정된 컨트랙트로 직접 조회만 수행
      console.log('--- 컨트랙트 직접 조회 시작 ---');
      
      const foundNFTs = await fetchNFTsFromContracts([NFT_CONTRACT_ADDRESS], targetAddress);
      console.log('직접 조회 결과:', foundNFTs.length, '개');
      
      if (foundNFTs.length > 0) {
        console.log('✅ NFT 발견!');
        setDebugInfo(`✅ ${foundNFTs.length}개의 NFT를 찾았습니다.`);
      } else {
        setDebugInfo('이 주소는 NFT를 보유하고 있지 않습니다.');
      }
      
      setNfts(foundNFTs);
      
    } catch (err) {
      console.error('❌ NFT 조회 오류:', err);
      setError(err.message || 'NFT를 불러오는데 실패했습니다.');
      setDebugInfo(`오류 발생: ${err.message}`);
      setNfts([]);
    } finally {
      setIsLoading(false);
      console.log('🏁 NFT 조회 완료');
    }
  };

  // 주소 검색 핸들러
  const handleSearchAddress = () => {
    console.log('🔍 주소 검색 버튼 클릭됨');
    console.log('입력된 주소:', searchAddress);
    
    const trimmedAddress = searchAddress.trim();
    
    if (!trimmedAddress) {
      console.log('주소가 비어있음. 기본 계정으로 설정:', account);
      setCurrentAddress(account);
      fetchNFTs(account);
      return;
    }
    
    // 주소 유효성 검사
    if (!/^0x[a-fA-F0-9]{40}$/.test(trimmedAddress)) {
      alert('유효한 주소 형식이 아닙니다. (0x로 시작하는 40자리 16진수)');
      return;
    }
    
    console.log('유효한 주소. 조회 시작:', trimmedAddress);
    setCurrentAddress(trimmedAddress);
    fetchNFTs(trimmedAddress);
  };

  // 내 지갑으로 돌아가기
  const handleResetToMyWallet = () => {
    console.log('내 지갑으로 리셋');
    setSearchAddress('');
    setCurrentAddress(account);
    fetchNFTs(account);
  };

  // 초기 로드
  useEffect(() => {
    console.log('MyNFT 마운트됨. 계정:', account);
    if (account) {
      fetchNFTs(account);
    }
  }, []); // 빈 배열로 한 번만 실행

  return (
    <div className="nft-gallery">
      <div className="gallery-header">
        <button onClick={onBack} className="back-btn">
          <ArrowLeft className="icon" />
          뒤로가기
        </button>
        <h1>NFT 컬렉션</h1>
        <button onClick={() => fetchNFTs(currentAddress)} disabled={isLoading} className="refresh-btn">
          <RefreshCw className={`icon ${isLoading ? 'spinning' : ''}`} />
        </button>
      </div>

      {/* 주소 검색 섹션 */}
      <div className="address-search-section">
        <h3 className="search-title">NFT 조회할 지갑 주소</h3>
        <div className="address-search-container">
          <input
            type="text"
            className="address-input"
            placeholder="지갑 주소 입력 (0x...)"
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchAddress();
              }
            }}
          />
          <button 
            onClick={handleSearchAddress}
            className="search-btn"
            disabled={isLoading}
          >
            <Search className="icon" />
            조회
          </button>
          {currentAddress !== account && (
            <button 
              onClick={handleResetToMyWallet}
              className="reset-btn"
              disabled={isLoading}
            >
              <RefreshCw className="icon" />
              내 지갑
            </button>
          )}
        </div>
        
        <div className="current-address-display">
          조회 중인 주소: <strong>{currentAddress}</strong>
          {currentAddress === account && (
            <span className="my-wallet-badge">내 지갑</span>
          )}
        </div>
      </div>

      <div className="gallery-info">
        <p className="info-text">
          WorldLand 블록체인에 배포된 ERC-721 NFT를 자동으로 탐색하여 조회합니다.
        </p>
        <p className="info-text small">
          * Explorer API를 통해 NFT 거래 내역을 분석하고, 블록체인에서 직접 조회합니다.
        </p>
        {debugInfo && (
          <p className="info-text small" style={{color: '#3498db', marginTop: '8px', fontWeight: '600'}}>
            🔍 {debugInfo}
          </p>
        )}
        {!isLoading && (
          <div style={{
            marginTop: '15px', 
            padding: '15px', 
            background: discoveredContracts.length > 0 ? '#e8f5e9' : '#fff3e0', 
            borderRadius: '8px', 
            border: discoveredContracts.length > 0 ? '1px solid #27ae60' : '1px solid #ff9800'
          }}>
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: discoveredContracts.length > 0 ? '10px' : '0'}}>
              <p style={{margin: '0', fontWeight: '600', color: discoveredContracts.length > 0 ? '#27ae60' : '#f57c00', fontSize: '14px'}}>
                📋 발견된 NFT 컨트랙트
              </p>
              <span style={{
                background: discoveredContracts.length > 0 ? '#27ae60' : '#ff9800',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '700'
              }}>
                {discoveredContracts.length}개
              </span>
            </div>
            {discoveredContracts.length > 0 ? (
              <div style={{display: 'grid', gap: '8px'}}>
                {discoveredContracts.map((contract, index) => (
                  <div 
                    key={contract}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      background: 'white',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontFamily: 'monospace'
                    }}
                  >
                    <span style={{color: '#2c3e50', fontWeight: '500'}}>
                      {index + 1}. {contract}
                    </span>
                    <button
                      onClick={() => window.open(`https://scan.worldland.foundation/address/${contract}`, '_blank')}
                      style={{
                        padding: '4px 10px',
                        background: '#3498db',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <ExternalLink size={12} />
                      보기
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{margin: '10px 0 0 0', fontSize: '13px', color: '#f57c00', textAlign: 'center'}}>
                조회를 완료했지만 NFT 컨트랙트를 찾지 못했습니다.
              </p>
            )}
          </div>
        )}
      </div>

      {isLoading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>NFT를 불러오는 중...</p>
          <p style={{fontSize: '12px', color: '#7f8c8d', marginTop: '10px'}}>
            {debugInfo}
          </p>
        </div>
      )}
      
      {error && (
        <div className="error-state">
          <AlertCircle className="icon" />
          <p>오류: {error}</p>
          <button onClick={() => fetchNFTs(currentAddress)} className="retry-btn">
            다시 시도
          </button>
        </div>
      )}
      
      {!isLoading && !error && (
        <>
          {nfts.length > 0 ? (
            <div className="nft-grid">
              {nfts.map((nft, index) => (
                <NFTCard 
                  key={`${nft.contractAddress}-${nft.tokenId}-${index}`} 
                  nft={nft} 
                  onClick={() => {
                    console.log('NFT 클릭:', nft);
                    fetchNFTDetailInfo(nft);
                  }} 
                />
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <ImageIcon className="empty-icon" />
              <p>보유한 NFT가 없습니다.</p>
              <p className="empty-hint">
                {currentAddress === account 
                  ? 'NFT를 발행하거나 받으면 여기에 표시됩니다.'
                  : '이 주소는 NFT를 보유하고 있지 않습니다.'}
              </p>
            </div>
          )}
        </>
      )}

      {/* NFT 상세 정보 모달 */}
      {selectedNFTDetail && (
        <NFTDetailModal 
          nftDetail={selectedNFTDetail}
          onClose={() => setSelectedNFTDetail(null)}
          isLoading={nftDetailLoading}
        />
      )}
    </div>
  );
};

export default MyNFT;